import type { CampaignWithCampTypesAndLocationsT } from "@/app/types/campaigns";
import type { OfferFormDataT, RelatedOfferMinimalT } from "@/app/types/offers";
import type { PublishedMakeT } from "@/app/services/server/frontend/get-published-makes";

export type BrandKeyT = "default" | "skoda" | "audi";
export type PublicBrandKeyT = Exclude<BrandKeyT, "default">;

export type BrandDefinitionT = {
  key: BrandKeyT;
  slug: string | null;
  routePrefix: string;
  displayName: string;
  seoTitleSuffix: string | null;
  allowedMakeNames: string[];
  themeAttributes: Record<string, string>;
};

const BRAND_DEFINITIONS: Record<BrandKeyT, BrandDefinitionT> = {
  default: {
    key: "default",
    slug: null,
    routePrefix: "",
    displayName: "Wiest Group",
    seoTitleSuffix: null,
    allowedMakeNames: [],
    themeAttributes: {
      "data-brand": "default",
    },
  },
  skoda: {
    key: "skoda",
    slug: "skoda",
    routePrefix: "/skoda",
    displayName: "Skoda",
    seoTitleSuffix: "Skoda",
    allowedMakeNames: ["skoda", "škoda"],
    themeAttributes: {
      "data-brand": "skoda",
    },
  },
  audi: {
    key: "audi",
    slug: "audi",
    routePrefix: "/audi",
    displayName: "Audi",
    seoTitleSuffix: "Audi",
    allowedMakeNames: ["audi"],
    themeAttributes: {
      "data-brand": "audi",
    },
  },
};

export const PUBLIC_BRAND_KEYS = Object.freeze(
  Object.values(BRAND_DEFINITIONS)
    .filter(
      (brand): brand is BrandDefinitionT & { key: PublicBrandKeyT } =>
        brand.key !== "default"
    )
    .map((brand) => brand.key)
);

const normalizeMakeName = (value?: string | number | null): string =>
  typeof value === "number"
    ? String(value)
    : (value?.trim().toLowerCase().replace(/\s+/g, " ") ?? "");

const isPublicBrandKey = (value: string): value is PublicBrandKeyT =>
  PUBLIC_BRAND_KEYS.includes(value as PublicBrandKeyT);

const getOfferMakeName = (
  offer: Pick<OfferFormDataT, "make"> | Pick<RelatedOfferMinimalT, "make">
): string => normalizeMakeName(offer.make?.name);

const getVehicleMakeName = (vehicle: {
  make?: string | number | null;
  makeLabel?: string | null;
}): string => normalizeMakeName(vehicle.makeLabel ?? vehicle.make);

export const getBrandConfig = (brandKey: BrandKeyT): BrandDefinitionT =>
  BRAND_DEFINITIONS[brandKey];

export const isPathUnderRoutePrefix = (
  pathname: string | null | undefined,
  routePrefix: string
): boolean => {
  if (!pathname || !routePrefix) {
    return false;
  }

  return pathname === routePrefix || pathname.startsWith(`${routePrefix}/`);
};

export const isBrandPath = (
  pathname: string | null | undefined,
  brandKey: PublicBrandKeyT
): boolean =>
  isPathUnderRoutePrefix(pathname, getBrandConfig(brandKey).routePrefix);

export const resolveBrandFromParams = (
  brand: string | null | undefined
): PublicBrandKeyT | null => {
  if (!brand) {
    return null;
  }

  const normalizedBrand = brand.trim().toLowerCase();
  return isPublicBrandKey(normalizedBrand) ? normalizedBrand : null;
};

export const buildBrandPath = (
  brandKey: BrandKeyT,
  pathname: string
): string => {
  const normalizedPath = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const routePrefix = getBrandConfig(brandKey).routePrefix;

  if (!routePrefix) {
    return normalizedPath;
  }

  return normalizedPath === "/"
    ? routePrefix
    : `${routePrefix}${normalizedPath}`;
};

export const isMakeAllowedForBrand = (
  makeName: string | null | undefined,
  brandKey: BrandKeyT
): boolean => {
  if (brandKey === "default") {
    return true;
  }

  const normalizedMakeName = normalizeMakeName(makeName);
  if (!normalizedMakeName) {
    return false;
  }

  return getBrandConfig(brandKey).allowedMakeNames.includes(normalizedMakeName);
};

export const isOfferAllowedForBrand = (
  offer: Pick<OfferFormDataT, "make"> | Pick<RelatedOfferMinimalT, "make">,
  brandKey: BrandKeyT
): boolean => isMakeAllowedForBrand(getOfferMakeName(offer), brandKey);

export const isVehicleAllowedForBrand = (
  vehicle: {
    make?: string | number | null;
    makeLabel?: string | null;
  },
  brandKey: BrandKeyT
): boolean => isMakeAllowedForBrand(getVehicleMakeName(vehicle), brandKey);

export const isCampaignAllowedForBrand = (
  campaign: Pick<CampaignWithCampTypesAndLocationsT, "related_offers">,
  brandKey: BrandKeyT
): boolean => {
  if (brandKey === "default") {
    return true;
  }

  return (campaign.related_offers ?? []).some((offer) =>
    isOfferAllowedForBrand(offer, brandKey)
  );
};

export const filterOffersForBrand = <T extends Pick<OfferFormDataT, "make">>(
  offers: T[],
  brandKey: BrandKeyT
): T[] => offers.filter((offer) => isOfferAllowedForBrand(offer, brandKey));

export const filterRelatedOffersForBrand = <
  T extends Pick<RelatedOfferMinimalT, "make">,
>(
  offers: T[],
  brandKey: BrandKeyT
): T[] => offers.filter((offer) => isOfferAllowedForBrand(offer, brandKey));

export const filterCampaignsForBrand = <
  T extends Pick<CampaignWithCampTypesAndLocationsT, "related_offers">,
>(
  campaigns: T[],
  brandKey: BrandKeyT
): T[] =>
  campaigns.filter((campaign) => isCampaignAllowedForBrand(campaign, brandKey));

export const filterMakesForBrand = (
  makes: PublishedMakeT[],
  brandKey: BrandKeyT
): PublishedMakeT[] =>
  brandKey === "default"
    ? makes
    : makes.filter((make) => isMakeAllowedForBrand(make.name, brandKey));
